import { Button, Grid, TextField, makeStyles } from "@material-ui/core";
import { Skeleton, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { useAuth } from "../../../../../../context/auth";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import { getAll } from "../../../../../../services/committePackages.service";
import { IGroup, getGroups } from "../../../../../../services/packageGroups.service";
import { Select } from "../../../../../Common/Select";
import SplitButton from "../../../../../Common/SplitButton";
import ToastMessage from "../../../../../Common/ToastMessage";
import GroupCard from "../../../components/Cards/GoupCard";
import Header from '../../../components/Header';
import { BoxesContainer, Container, CounterLabel, FetchMetaDataContainer, FilterButtonsContainer, FilterInputs, ListCounterContainer, ListOrderContainer } from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
}));

interface IBusinessUnitProps {
  actionShowSpinner: (value: boolean) => void;
}

const Groups: React.FC<IBusinessUnitProps> = ({ actionShowSpinner }) => {
  const { t } = useTranslation();

  const { userInfo } = useAuth() as any;
  const history = useHistory();
  const classes = useStyles();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [committeePackages, setCommitteePackages] = useState<{id: string; name: string}[]>([]);
  const [filters, setFilters] = useState<{
    name?: string;
    committeePackage?: {id: string; name: string};
  }>({});
  
  const fetchGroups = useCallback(async() => {
    actionShowSpinner(true);
    setLoading(true);
    
    try{
      const { groups } = await getGroups();
    
      setGroups(groups);
    }catch(error: any) {
      const [ errorMessage ] = error?.response?.data?.errors || [t("softwareManagement.modules.groups.errorFetchGroups")];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: 'error',
        autoHideDuration: 4000,
      });
    }    

    actionShowSpinner(false);
    setLoading(false);
  }, []);

  const fetchCommitteePackages = useCallback(async () => {
    actionShowSpinner(true);
    
    try{
      const { committeePackages: committeePackagesOptions } = await getAll()

      setCommitteePackages(committeePackagesOptions);
    }catch(error: any) {
      const [ errorMessage ] = error?.response?.data?.errors || [t("softwareManagement.modules.business.errorFetchCommitteePackages")];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: 'error',
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchCommitteePackages();
  }, []);

  const handleFilterGroups = useCallback(async () => {
    actionShowSpinner(true);
    setLoading(true);

    const { groups } = await getGroups({name: filters.name, committeePackageId: filters.committeePackage?.id});

    setGroups(groups);

    actionShowSpinner(false);
    setLoading(false);
  }, [filters]);

  const handleCleanFilters = useCallback(async () => {
    actionShowSpinner(true);
    setLoading(true);

    fetchGroups();
    setFilters({});
  }, [filters]);

  return (
    <Container>
      <Header 
        title={t("softwareManagement.modules.groups.titlePlural")}
        showVectors
        showMenuToggle
        filterInputs={
          <>
            <FilterInputs container direction='column' spacing={2}>
              <Grid style={{padding: '0 10px 0 10px'}} item lg={6} xs={6}>
                <TextField
                  name="name"
                  id="name"
                  label={t("softwareManagement.name")}
                  value={filters.name}
                  onChange={(e) => setFilters({...filters, name: e.target.value})}
                  fullWidth
                  autoComplete="nope"
                />
              </Grid>
            
              <Grid style={{padding: '0 10px 0 10px'}} item lg={6} xs={6}>
                <Select
                  id="committee"
                  options={committeePackages}
                  value={filters.committeePackage}
                  setValue={(value: any) => setFilters({...filters, committeePackage: value})}
                  label={t('softwareManagement.modules.committeePackage.title')}
                />
              </Grid>
            </FilterInputs>
            
            <FilterButtonsContainer>
              <Button
                variant="outlined"
                onClick={handleCleanFilters}
              >
                {t("softwareManagement.clean")}
              </Button>

              <Button
                variant="contained"
                className={classes.button}
                onClick={handleFilterGroups}
              >
                {t("softwareManagement.search")}
              </Button>
            </FilterButtonsContainer>
          </>
        }
      />
      
      <FetchMetaDataContainer>
        {loading ? (
          <Stack style={{width: '100%'}} spacing={1}>
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height={60} />
          </Stack>
        ) : (
          <>
            <ListCounterContainer>
              <CounterLabel>
                {`${groups.length} ${t("softwareManagement.modules.groups.titlePlural")} ${t("softwareManagement.found").toLowerCase()}`}
              </CounterLabel>
            </ListCounterContainer>

            <ListOrderContainer>
              <SplitButton 
                principalButton={{
                  label: t("softwareManagement.modules.groups.createGroups"),
                  onClick: () => history.push("/system-management/configure-groups")
                }}
                options={[
                  {
                    label: t("softwareManagement.modules.committeePackage.createPackage"),
                    onClick: () => history.push("/system-management/configure-committee-packages")
                  },
                  {
                    label: t("softwareManagement.modules.business.createBusiness"),
                    disabled: userInfo?.master !== undefined ? !userInfo?.master : false,
                    onClick: () => history.push("/system-management/configure-business")
                  }
                ]}
              />
            </ListOrderContainer>
          </>
        )}
      </FetchMetaDataContainer>

      <BoxesContainer>
        {loading ? (
          <>
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height='200px' />
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height='200px' />
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height='200px' />
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height='200px' />
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height='200px' />
            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px' }} width='100%' height='200px' />
          </>
        ) : (
          groups.map(group => (
            <GroupCard 
              key={group.id}
              group={{
                id: group.id,
                name: group.name,
                active: group.active,
                committeePackage: {
                  id: group.committeePackageId,
                  active: group.committeePackageActive,
                  name: group.committeePackageName,
                  committees: group.committees.map(committee => ({
                    id: committee.id,
                    name: committee.name,
                    activated: committee.activated,
                  }))
                }
              }}
            />
          ))
        )}
      </BoxesContainer>
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || 'error'}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const GroupsConnection = connect(
  null,
  mapDispatchToProps
)(Groups);

export default GroupsConnection;
